import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { reviewKeys } from '../queries/keys';
import { reviewPaths } from '../queries/paths';
import { readReviewCard, readRounds } from '../utils/delivery';
import { taskKeys } from '../../tasks/queries/keys';
import { ALL_MODALITIES, taskPaths } from '../../tasks/queries/paths';

// TSS vite/03 — server state for the review queue.
//
// The tasks waiting for review, and for each one three reads, bounded by what
// is actually in `in_review` — a queue somebody is expected to drain:
//
//   the detail PACKAGE   commits (with author and date), criteria, scope,
//                        previous returns, the reservation, the repository
//   its review rounds    the exact version, the criteria as delivered, the
//                        AI's evaluation per criterion, the log
//   its progress notes   what the agent reported while working — only with
//                        `events:read`, and the card says so without it

const PAGE = 50;
const PROGRESS_LIMIT = 30;

export function useReviewQueue({ projectId } = {}) {
    const { can } = useAuth();
    const canReadEvents = can('events:read');

    const params = useMemo(() => {
        // Both modalities: a one-off task is reviewed like any other.
        const query = { limit: PAGE, offset: 0, workflow_state: 'in_review', modality: ALL_MODALITIES };
        if (projectId) query.project_id = projectId;
        return query;
    }, [projectId]);

    const waiting = useQuery({
        queryKey: reviewKeys.waiting(params),
        queryFn: ({ signal }) => api
            .get(reviewPaths.waiting(), { params, signal })
            .then(response => response.data),
    });

    const tasks = useMemo(() => waiting.data?.items ?? [], [waiting.data]);

    // THE WHOLE PACKAGE, kept whole. Same key as the task screens, so opening
    // a card from here costs nothing.
    const details = useQueries({
        queries: tasks.map(task => ({
            queryKey: taskKeys.detail(String(task.id)),
            queryFn: ({ signal }) => api.get(taskPaths.detail(task.id), { signal }).then(r => r.data),
            staleTime: 60_000,
        })),
    });

    const rounds = useQueries({
        queries: tasks.map(task => ({
            queryKey: reviewKeys.rounds(task.id),
            queryFn: ({ signal }) => api.get(reviewPaths.rounds(task.id), { signal }).then(r => r.data?.items ?? []),
            staleTime: 30_000,
        })),
    });

    const progress = useQueries({
        queries: tasks.map(task => ({
            queryKey: reviewKeys.progress(task.id),
            queryFn: ({ signal }) => api
                .get(reviewPaths.events(), {
                    params: { task_id: task.id, kind: 'task.progress', limit: PROGRESS_LIMIT, offset: 0 },
                    signal,
                })
                .then(r => r.data?.items ?? []),
            enabled: canReadEvents,
            staleTime: 60_000,
        })),
    });

    const cards = useMemo(() => tasks.map((row, index) => {
        const card = readReviewCard({
            detail: details[index]?.data,
            rounds: rounds[index]?.data,
            progress: progress[index]?.data,
        });
        return {
            ...card,
            // The list row until the package arrives, so the title is never blank.
            task: details[index]?.data ? card.task : row,
            isLoading: !!(details[index]?.isLoading || rounds[index]?.isLoading),
            roundsError: rounds[index]?.error ?? null,
        };
    }), [tasks, details, rounds, progress]);

    return {
        cards,
        canReadEvents,
        loaded: tasks.length,
        total: waiting.data?.pagination?.total ?? 0,
        isLoading: waiting.isLoading,
        error: waiting.error,
    };
}

// TODAS LAS RONDAS DE UNA TAREA, para leerlas desde la tarea y no solo desde la
// cola. Es `GET /admin/tasks/{id}/review-rounds` con `tasks:read` — el mismo
// permiso que ya costó abrir la tarea, así que quien llegó hasta acá lo tiene.
//
// MISMA CLAVE QUE LA COLA (`reviewKeys.rounds`): abrir una tarea desde la cola
// no vuelve a pedirlas, y una decisión las invalida en los dos lugares a la vez.
export function useTaskRounds(taskId, { enabled = true } = {}) {
    const query = useQuery({
        queryKey: reviewKeys.rounds(taskId),
        queryFn: ({ signal }) => api
            .get(reviewPaths.rounds(taskId), { signal })
            .then(response => response.data?.items ?? []),
        enabled: enabled && !!taskId,
        staleTime: 30_000,
    });

    const rounds = useMemo(() => readRounds(query.data), [query.data]);

    return { rounds, isLoading: query.isLoading, error: query.error };
}

// The decision: `{ review_round_id, result: 'approved' | 'returned', reason }`.
//
// NO OPTIMISTIC UPDATE. Every gate that can refuse it lives in Core — a human
// session, QA capacity in the project, the round still being the open one, a
// versioned delivery with its criteria — and painting a verdict that then
// comes back 403 or 409 would show a decision that never happened.
export function useDecideRound(taskId) {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (payload) => api
            .post(reviewPaths.decision(taskId), payload)
            .then(response => response.data),
        onSuccess: () => {
            // `tasks` as a whole: an approved task leaves the queue, and the
            // board, the list and the detail all read rows under that key.
            client.invalidateQueries({ queryKey: reviewKeys.all });
            client.invalidateQueries({ queryKey: ['events'] });
        },
        // The round that was read is no longer the open one: somebody delivered
        // again. Read it afresh, so the next decision cites the right round.
        onError: (err) => {
            if (err?.status === 409) {
                client.invalidateQueries({ queryKey: reviewKeys.rounds(taskId) });
                client.invalidateQueries({ queryKey: reviewKeys.all });
            }
        },
    });
}
